import { StringQ } from "@pdfking/structs"
import type { Bytes } from "@pdfking/structs"

import type { BinaryInput } from "#public"

import { Reduce } from "../functions/Reduce"
import { ToArray } from "../functions/ToArray"
import { StringToUInt8 } from "./StringToUInt8"

/**
 * Merges whatever bytes it is handed into a single Uint8Array.
 *
 * @param input - Bytes, or the pieces they arrive in
 * @param bytes - Optional total size; if provided skips computing length
 * @returns A new Uint8Array containing all input bytes concatenated
 *
 * @example
 * ```ts
 * import { UInt8Merge } from "@pdfking/tfunc"
 *
 * const a = new Uint8Array([1, 2, 3]);
 * const b = new Uint8Array([4, 5, 6]);
 * const merged = UInt8Merge([a, b]);
 * console.log(merged); // Uint8Array([1, 2, 3, 4, 5, 6])
 *
 * // ArrayBuffers and strings are bytes too
 * UInt8Merge([a, new ArrayBuffer(2)]);
 * UInt8Merge("hello");
 *
 * // With known total size
 * const fast = UInt8Merge([a, b], 6);
 * ```
 */

// Everything becomes the one shape the merge works in — a string is bytes
// nobody encoded yet, and an ArrayBuffer is bytes with no view over them.
const toArray = (value: BinaryInput): Uint8Array =>
  StringQ(value)
    ? StringToUInt8(value)
    : value instanceof ArrayBuffer
      ? new Uint8Array(value)
      : value

export const UInt8Merge = (
  input: BinaryInput | BinaryInput[],
  bytes?: Bytes
): Uint8Array<ArrayBuffer> => {
  const arrays = ToArray(input).map(toArray)
  const result = new Uint8Array(
    bytes ?? Reduce((sum, view: Uint8Array) => sum + view.length, arrays, 0)
  )

  let offset = 0
  for (const view of arrays) {
    result.set(view, offset)
    offset += view.length
  }
  return result
}
